/**
 * AdminLogin — Simple token entry page for admin access.
 * Validates token against the backend before granting access.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../context/AdminContext';
import { dashboardApi } from '../services/api';

export function AdminLogin() {
  const [inputToken, setInputToken] = useState('');
  const [role, setRole] = useState('vm');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated } = useAdmin();
  const navigate = useNavigate();

  // Already logged in → redirect
  if (isAuthenticated) {
    navigate('/admin/dashboard', { replace: true });
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputToken.trim()) {
      setError('Please enter a token');
      return;
    }

    setLoading(true);
    setError('');

    // Temporarily store token so the API interceptor can use it
    localStorage.setItem('serve_admin_token', inputToken.trim());

    try {
      const res = await dashboardApi.getStats(1, 1);
      if (res.status === 'success') {
        // Both ADMIN_TOKEN (tech) and DASHBOARD_API_KEY (vm) are accepted.
        // We store the token and let the AdminShell determine visibility.
        // Convention: tech team uses ADMIN_TOKEN, VM team uses DASHBOARD_API_KEY.
        // The frontend can't know which — so we add a simple role selector.
        login(inputToken.trim(), role);
        navigate('/admin/dashboard');
      } else {
        localStorage.removeItem('serve_admin_token');
        setError('Invalid token — access denied');
      }
    } catch (err) {
      localStorage.removeItem('serve_admin_token');
      if (err.response?.status === 401) {
        setError('Invalid token — access denied');
      } else {
        setError('Unable to connect. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="max-w-sm w-full">
        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-lg bg-blue-600 flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-xl">S</span>
          </div>
          <h1 className="text-xl font-semibold text-slate-900">SERVE Admin</h1>
          <p className="text-sm text-slate-500 mt-1">Enter your admin token to continue</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <input
              type="password"
              value={inputToken}
              onChange={(e) => { setInputToken(e.target.value); setError(''); }}
              placeholder="Admin token"
              className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
              disabled={loading}
            />
            {error && <p className="text-xs text-red-500 mt-1.5 font-medium">{error}</p>}
          </div>
          <div>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            >
              <option value="vm">Operations Team</option>
              <option value="tech">Tech Team</option>
            </select>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {loading ? 'Verifying…' : 'Enter'}
          </button>
        </form>

        <p className="text-center text-xs text-slate-400 mt-6">
          <a href="/" className="hover:text-slate-600">← Back to volunteer chat</a>
        </p>
      </div>
    </div>
  );
}

export default AdminLogin;
